import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  BarChart3,
  Cloud,
  Compass,
  CreditCard,
  GitBranch,
  KeyRound,
  LayoutDashboard,
  Radar,
  Rocket,
  Sparkles,
} from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import { useAuth } from '../lib/auth'

type Plan = {
  plan_id: string
  name: string
  tagline: string
  price_usd_month: number | null
  ai_credits_month: number | null
  clouds: string[]
  market_fit: string
}

type Question = {
  id: string
  section: string
  prompt: string
  type: string
  options?: string[]
  required?: boolean
}

type BoardLane = { column: string; cards: { card_id: string; title: string; build_id?: string }[] }

type EnterpriseOverview = {
  subscription: { plan_id: string; status: string }
  plan: Plan
  catalog: { plans: Plan[]; research_notes: string }
  deploy_targets: { cloud: string; label: string; allowed_on_plan: boolean; regions_default: string[] }[]
  discovery: { questionnaire: { questions: Question[] }; sessions: { discovery_id: string; completeness_pct: number; status: string }[] }
  board: { lanes: BoardLane[]; card_count: number }
  usage: {
    total_tokens: number
    total_cost_usd: number
    credits_used: number
    credits_included: number | null
    estimated_overage_usd: number
    by_model: { provider: string; model: string; cost_usd: number; tokens: number }[]
  }
  payment_options?: {
    methods: { id: string; label: string; allowed: boolean }[]
    plan: { name: string; price_usd_month: number | null }
  }
  password_policy?: {
    min_length: number
    max_failed_attempts: number
    lockout_minutes: number
  }
  analytics?: {
    app_count: number
    avg_success_pct: number
    mvp_ready_count: number
    grade_histogram: Record<string, number>
    builds: { build_id: string; grade: string; success_pct: number; mvp_ready: boolean; idea?: string }[]
  }
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  })
  if (!res.ok) throw new Error(await res.text())
  return res.json() as Promise<T>
}

export function EnterprisePage() {
  const { user, loading } = useAuth()
  const [data, setData] = useState<EnterpriseOverview | null>(null)
  const [answers, setAnswers] = useState<Record<string, string | boolean>>({})
  const [activeDiscovery, setActiveDiscovery] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [signupPlan, setSignupPlan] = useState('builder')
  const [cardNumber, setCardNumber] = useState('4242424242424242')
  const [resetEmail, setResetEmail] = useState('')
  const [cicdBuildId, setCicdBuildId] = useState('')
  const [cicdStatus, setCicdStatus] = useState<{
    status: string
    results?: { stage: string; status: string }[]
    human_gates?: Record<string, string>
  } | null>(null)

  const questions = useMemo(
    () => data?.discovery.questionnaire.questions ?? [],
    [data],
  )

  async function refresh() {
    const overview = await api<EnterpriseOverview>('/api/afos360/enterprise')
    setData(overview)
    const latest = overview.discovery.sessions.at(-1)
    if (latest) setActiveDiscovery(latest.discovery_id)
  }

  useEffect(() => {
    if (user) {
      void refresh().catch((err) => setError(err instanceof Error ? err.message : 'Load failed'))
    }
  }, [user])

  if (!loading && !user) return <Navigate to="/signin" replace />

  async function startDiscovery() {
    setBusy(true)
    setError(null)
    try {
      const row = await api<{ discovery_id: string }>('/api/afos360/discovery', {
        method: 'POST',
        body: JSON.stringify({ title: 'Enterprise app discovery' }),
      })
      setActiveDiscovery(row.discovery_id)
      setMessage(`Discovery ${row.discovery_id} opened`)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Discovery failed')
    } finally {
      setBusy(false)
    }
  }

  async function saveAnswers(e: FormEvent) {
    e.preventDefault()
    if (!activeDiscovery) return
    setBusy(true)
    setError(null)
    try {
      const payload: Record<string, string | boolean> = {}
      for (const q of questions) {
        const val = answers[q.id]
        if (val === undefined || val === '') continue
        if (q.type === 'boolean') payload[q.id] = val === true || val === 'true'
        else payload[q.id] = val
      }
      const row = await api<{ completeness_pct: number; status: string }>(
        `/api/afos360/discovery/${activeDiscovery}/answers`,
        { method: 'POST', body: JSON.stringify({ answers: payload }) },
      )
      setMessage(`Saved — ${row.completeness_pct}% complete (${row.status})`)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  async function launch() {
    if (!activeDiscovery) return
    setBusy(true)
    setError(null)
    try {
      const result = await api<{ build_id: string; deploy?: { cloud?: string; error?: string } }>(
        `/api/afos360/discovery/${activeDiscovery}/launch`,
        { method: 'POST' },
      )
      setMessage(
        `Build ${result.build_id} launched` +
          (result.deploy?.cloud ? ` · deploy target ${result.deploy.cloud}` : '') +
          (result.deploy?.error ? ` · ${result.deploy.error}` : ''),
      )
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Launch failed')
    } finally {
      setBusy(false)
    }
  }

  async function checkout(planId: string) {
    setBusy(true)
    setError(null)
    try {
      const result = await api<{ checkout?: { url?: string; mode?: string }; plan_id: string }>(
        '/api/afos360/billing/checkout',
        { method: 'POST', body: JSON.stringify({ plan_id: planId }) },
      )
      setMessage(
        result.checkout?.mode === 'dry_run'
          ? `Plan ${result.plan_id} activated (Stripe dry-run)`
          : `Checkout ready for ${result.plan_id}`,
      )
      if (result.checkout?.url && result.checkout.mode === 'stripe') {
        window.location.href = result.checkout.url
        return
      }
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout failed')
    } finally {
      setBusy(false)
    }
  }

  async function cardSignup(e: FormEvent) {
    e.preventDefault()
    if (!user?.email) return
    setBusy(true)
    setError(null)
    try {
      const result = await api<{
        status: string
        plan_id: string
        card?: { last4: string; brand: string }
        mode?: string
      }>('/api/afos360/payments/signup', {
        method: 'POST',
        body: JSON.stringify({
          plan_id: signupPlan,
          email: user.email,
          name: user.name,
          payment_method: 'card',
          card_number: cardNumber,
          exp_month: 12,
          exp_year: 2030,
          cvc: '123',
        }),
      })
      setMessage(
        `Signup ${result.status} for ${result.plan_id}` +
          (result.card ? ` · ${result.card.brand} •••• ${result.card.last4}` : '') +
          (result.mode === 'dry_run' ? ' (dry-run)' : ''),
      )
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signup failed')
    } finally {
      setBusy(false)
    }
  }

  async function runCicd() {
    const buildId = cicdBuildId || data?.analytics?.builds?.[0]?.build_id
    if (!buildId) {
      setError('Launch a build first, or enter a build_id')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const result = await api<{
        status: string
        result?: { status: string; results?: { stage: string; status: string }[]; human_gates?: Record<string, string> }
      }>('/api/afos360/v1/cicd/run', {
        method: 'POST',
        body: JSON.stringify({ build_id: buildId, mode: 'dry_run' }),
      })
      const loop = result.result || (result as unknown as { status: string; results?: { stage: string; status: string }[]; human_gates?: Record<string, string> })
      setCicdBuildId(buildId)
      setCicdStatus(loop)
      setMessage(`CI/CD ${loop.status}`)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'CI/CD failed')
    } finally {
      setBusy(false)
    }
  }

  async function decideCicd(gate: 'certify' | 'deploy_poc', decision: 'approve' | 'reject') {
    if (!cicdBuildId) return
    setBusy(true)
    setError(null)
    try {
      const loop = await api<{
        status: string
        results?: { stage: string; status: string }[]
        human_gates?: Record<string, string>
      }>(`/api/afos360/v1/cicd/${cicdBuildId}/decide`, {
        method: 'POST',
        body: JSON.stringify({ gate, decision, note: `${decision} via enterprise UI` }),
      })
      setCicdStatus(loop)
      setMessage(`Gate ${gate} ${decision} → ${loop.status}`)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Decision failed')
    } finally {
      setBusy(false)
    }
  }

  async function startPasswordReset(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await api<{
        reset_id: string
        otp_demo?: string
        mail: { status: string }
      }>('/api/afos360/password/reset/start', {
        method: 'POST',
        body: JSON.stringify({ email: resetEmail || user?.email }),
      })
      setMessage(
        `Password reset started (${result.mail.status})` +
          (result.otp_demo ? ` · demo OTP ${result.otp_demo}` : ''),
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppChrome>
      <main className="afos-page space-y-6">
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-3xl p-4 sm:p-6"
        >
          <div className="mb-2 flex flex-wrap items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold sm:text-2xl">
            <Compass className="h-5 w-5 text-[var(--color-cyan)]" />
            Enterprise app factory
          </div>
          <p className="max-w-3xl text-sm text-[var(--color-mist)]">
            Discovery questions (product, customers, revenue, technical), Stripe plan levels,
            AWS/GCP deploy choice, board tracking per project, and AI model cost/usage —
            gated by your subscription.
          </p>
          {data && (
            <p className="mt-3 text-xs uppercase tracking-wider text-[var(--color-amber)]">
              Current plan {data.subscription.plan_id} · {data.plan.tagline}
            </p>
          )}
          {message && (
            <p className="mt-3 rounded-2xl border border-[rgba(46,230,197,0.35)] bg-[rgba(46,230,197,0.08)] px-3 py-2 text-sm text-[var(--color-cyan)]">
              {message}
            </p>
          )}
          {error && (
            <p className="mt-3 rounded-2xl border border-[rgba(255,107,138,0.35)] bg-[rgba(255,107,138,0.08)] px-3 py-2 text-sm text-[var(--color-rose)]">
              {error}
            </p>
          )}
        </motion.section>

        <section className="grid gap-6 lg:grid-cols-2">
          <div className="glass rounded-3xl p-6">
            <h2 className="mb-3 flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
              <CreditCard className="h-5 w-5 text-[var(--color-amber)]" />
              Marketable plans
            </h2>
            <p className="mb-4 text-xs text-[var(--color-mist)]">{data?.catalog.research_notes}</p>
            <div className="space-y-3">
              {(data?.catalog.plans || []).map((plan) => (
                <div
                  key={plan.plan_id}
                  className="rounded-2xl border border-[var(--color-line)] bg-black/20 px-4 py-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold">
                        {plan.name}{' '}
                        <span className="text-xs text-[var(--color-mist)]">
                          {plan.price_usd_month == null
                            ? 'custom'
                            : plan.price_usd_month === 0
                              ? 'free'
                              : `$${plan.price_usd_month}/mo`}
                        </span>
                      </div>
                      <div className="text-xs text-[var(--color-mist)]">{plan.tagline}</div>
                      <div className="mt-1 text-[11px] text-[var(--color-cyan)]">
                        credits {plan.ai_credits_month ?? 'negotiated'} · clouds{' '}
                        {plan.clouds.join(', ') || 'none'}
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={busy || data?.subscription.plan_id === plan.plan_id}
                      onClick={() => void checkout(plan.plan_id)}
                      className="rounded-full border border-[var(--color-line)] px-3 py-1 text-xs font-semibold disabled:opacity-40"
                    >
                      {data?.subscription.plan_id === plan.plan_id ? 'Current' : 'Select'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="glass rounded-3xl p-6">
            <h2 className="mb-3 flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
              <Cloud className="h-5 w-5 text-[var(--color-cyan)]" />
              Build & deploy clouds
            </h2>
            <div className="space-y-3">
              {(data?.deploy_targets || []).map((t) => (
                <div
                  key={t.cloud}
                  className="rounded-2xl border border-[var(--color-line)] bg-black/20 px-4 py-3"
                >
                  <div className="font-semibold">
                    {t.label}{' '}
                    <span className="text-xs uppercase tracking-wider text-[var(--color-mist)]">
                      {t.allowed_on_plan ? 'unlocked' : 'upgrade required'}
                    </span>
                  </div>
                  <div className="text-xs text-[var(--color-mist)]">
                    Regions: {t.regions_default.join(', ')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="glass rounded-3xl p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
              <Sparkles className="h-5 w-5 text-[var(--color-amber)]" />
              Requirements discovery
            </h2>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => void startDiscovery()}
                className="rounded-full bg-[var(--color-foam)] px-4 py-2 text-sm font-semibold text-[var(--color-void)]"
              >
                Start discovery
              </button>
              <button
                type="button"
                disabled={busy || !activeDiscovery}
                onClick={() => void launch()}
                className="inline-flex items-center gap-2 rounded-full border border-[var(--color-line)] px-4 py-2 text-sm font-semibold"
              >
                <Rocket className="h-4 w-4" />
                Launch build
              </button>
            </div>
          </div>
          <form className="grid gap-3 md:grid-cols-2" onSubmit={saveAnswers}>
            {questions.map((q) => (
              <label key={q.id} className="block text-sm">
                <span className="mb-1 block text-[var(--color-mist)]">
                  {q.prompt}
                  {q.required ? ' *' : ''}
                </span>
                {q.type === 'select' ? (
                  <select
                    className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-3 py-2"
                    value={String(answers[q.id] ?? '')}
                    onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                  >
                    <option value="">Select…</option>
                    {(q.options || []).map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : q.type === 'boolean' ? (
                  <select
                    className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-3 py-2"
                    value={String(answers[q.id] ?? '')}
                    onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                  >
                    <option value="">Select…</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                ) : (
                  <textarea
                    className="min-h-[72px] w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-3 py-2"
                    value={String(answers[q.id] ?? '')}
                    onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                  />
                )}
              </label>
            ))}
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={busy || !activeDiscovery}
                className="rounded-full border border-[var(--color-line)] px-4 py-2 text-sm font-semibold disabled:opacity-40"
              >
                Save answers
              </button>
            </div>
          </form>
        </section>

        <section className="grid gap-6 lg:grid-cols-2">
          <div className="glass rounded-3xl p-6">
            <h2 className="mb-4 flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
              <LayoutDashboard className="h-5 w-5 text-[var(--color-cyan)]" />
              Board tracking
            </h2>
            <div className="grid gap-2 sm:grid-cols-2">
              {(data?.board.lanes || []).map((lane) => (
                <div
                  key={lane.column}
                  className="rounded-2xl border border-[var(--color-line)] bg-black/20 p-3"
                >
                  <div className="mb-2 text-xs uppercase tracking-wider text-[var(--color-mist)]">
                    {lane.column}
                  </div>
                  {lane.cards.length === 0 ? (
                    <div className="text-xs text-[var(--color-mist)]">Empty</div>
                  ) : (
                    lane.cards.map((card) => (
                      <div key={card.card_id} className="mb-1 text-sm font-medium">
                        {card.title}
                      </div>
                    ))
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="glass rounded-3xl p-6">
            <h2 className="mb-4 flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
              <Radar className="h-5 w-5 text-[var(--color-amber)]" />
              AI model cost & usage
            </h2>
            {data?.usage ? (
              <>
                <div className="mb-3 grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded-2xl border border-[var(--color-line)] px-3 py-2">
                    Tokens {data.usage.total_tokens}
                  </div>
                  <div className="rounded-2xl border border-[var(--color-line)] px-3 py-2">
                    Cost ${data.usage.total_cost_usd}
                  </div>
                  <div className="rounded-2xl border border-[var(--color-line)] px-3 py-2">
                    Credits {data.usage.credits_used}
                    {data.usage.credits_included != null
                      ? ` / ${data.usage.credits_included}`
                      : ''}
                  </div>
                  <div className="rounded-2xl border border-[var(--color-line)] px-3 py-2">
                    Overage ${data.usage.estimated_overage_usd}
                  </div>
                </div>
                <div className="space-y-2">
                  {(data.usage.by_model || []).map((row) => (
                    <div
                      key={`${row.provider}-${row.model}`}
                      className="rounded-2xl border border-[var(--color-line)] bg-black/20 px-3 py-2 text-sm"
                    >
                      {row.provider}/{row.model} · {row.tokens} tok · ${row.cost_usd}
                    </div>
                  ))}
                  {(data.usage.by_model || []).length === 0 && (
                    <p className="text-sm text-[var(--color-mist)]">
                      No model spend yet — dry-run launches seed a zero-cost event.
                    </p>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-[var(--color-mist)]">Bind a project scope to see usage.</p>
            )}
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-2">
          <div className="glass rounded-3xl p-6">
            <h2 className="mb-3 flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
              <CreditCard className="h-5 w-5 text-[var(--color-cyan)]" />
              Card signup
            </h2>
            <p className="mb-3 text-xs text-[var(--color-mist)]">
              Payment options:{' '}
              {(data?.payment_options?.methods || [])
                .filter((m) => m.allowed)
                .map((m) => m.label)
                .join(' · ') || 'loading…'}
            </p>
            <form className="space-y-3" onSubmit={cardSignup}>
              <select
                className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-3 py-2"
                value={signupPlan}
                onChange={(e) => setSignupPlan(e.target.value)}
              >
                <option value="spark">Spark (free)</option>
                <option value="builder">Builder</option>
                <option value="growth">Growth</option>
              </select>
              <input
                className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-3 py-2 font-[family-name:var(--font-mono)]"
                value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value)}
                placeholder="Card number (test 4242…)"
                autoComplete="off"
              />
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-full bg-[var(--color-foam)] px-4 py-2 text-sm font-semibold text-[var(--color-void)]"
              >
                Sign up with card
              </button>
            </form>
          </div>

          <div className="glass rounded-3xl p-6">
            <h2 className="mb-3 flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
              <KeyRound className="h-5 w-5 text-[var(--color-amber)]" />
              Password reset & lockout
            </h2>
            <p className="mb-3 text-xs text-[var(--color-mist)]">
              Policy: min {data?.password_policy?.min_length ?? 12} chars · lock after{' '}
              {data?.password_policy?.max_failed_attempts ?? 5} fails for{' '}
              {data?.password_policy?.lockout_minutes ?? 15}m
            </p>
            <form className="space-y-3" onSubmit={startPasswordReset}>
              <input
                className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-3 py-2"
                type="email"
                placeholder={user?.email || 'email@company.com'}
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-full border border-[var(--color-line)] px-4 py-2 text-sm font-semibold"
              >
                Email reset OTP
              </button>
            </form>
          </div>
        </section>

        <section className="glass rounded-3xl p-6">
          <h2 className="mb-3 flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
            <GitBranch className="h-5 w-5 text-[var(--color-cyan)]" />
            Agile CI/CD loop
          </h2>
          <p className="mb-3 text-xs text-[var(--color-mist)]">
            Event + API based: build → test → learn → adjust → enhance → bug_fix → test →
            test → certify → deploy POC (human review)
          </p>
          <div className="mb-3 flex flex-wrap gap-2">
            <input
              className="min-w-[220px] flex-1 rounded-2xl border border-[var(--color-line)] bg-black/30 px-3 py-2 font-[family-name:var(--font-mono)] text-sm"
              placeholder={data?.analytics?.builds?.[0]?.build_id || 'build_…'}
              value={cicdBuildId}
              onChange={(e) => setCicdBuildId(e.target.value)}
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => void runCicd()}
              className="rounded-full bg-[var(--color-foam)] px-4 py-2 text-sm font-semibold text-[var(--color-void)]"
            >
              Run loop
            </button>
            <button
              type="button"
              disabled={busy || !cicdBuildId}
              onClick={() => void decideCicd('certify', 'approve')}
              className="rounded-full border border-[var(--color-line)] px-4 py-2 text-sm font-semibold"
            >
              Approve certify
            </button>
            <button
              type="button"
              disabled={busy || !cicdBuildId}
              onClick={() => void decideCicd('deploy_poc', 'approve')}
              className="rounded-full border border-[var(--color-line)] px-4 py-2 text-sm font-semibold"
            >
              Approve deploy POC
            </button>
          </div>
          {cicdStatus && (
            <div className="space-y-2">
              <div className="text-sm text-[var(--color-amber)]">Status: {cicdStatus.status}</div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                {(cicdStatus.results || []).map((row) => (
                  <div
                    key={row.stage}
                    className="rounded-2xl border border-[var(--color-line)] bg-black/20 px-3 py-2 text-xs"
                  >
                    <div className="font-semibold">{row.stage}</div>
                    <div className="text-[var(--color-mist)]">{row.status}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="glass rounded-3xl p-6">
          <h2 className="mb-4 flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
            <BarChart3 className="h-5 w-5 text-[var(--color-cyan)]" />
            App success analytics
          </h2>
          {data?.analytics ? (
            <>
              <div className="mb-4 grid gap-2 sm:grid-cols-4 text-sm">
                <div className="rounded-2xl border border-[var(--color-line)] px-3 py-2">
                  Apps {data.analytics.app_count}
                </div>
                <div className="rounded-2xl border border-[var(--color-line)] px-3 py-2">
                  Avg success {data.analytics.avg_success_pct}%
                </div>
                <div className="rounded-2xl border border-[var(--color-line)] px-3 py-2">
                  MVP ready {data.analytics.mvp_ready_count}
                </div>
                <div className="rounded-2xl border border-[var(--color-line)] px-3 py-2">
                  Grades{' '}
                  {Object.entries(data.analytics.grade_histogram)
                    .map(([g, n]) => `${g}:${n}`)
                    .join(' ')}
                </div>
              </div>
              <div className="space-y-2">
                {(data.analytics.builds || []).map((build) => (
                  <div
                    key={build.build_id}
                    className="rounded-2xl border border-[var(--color-line)] bg-black/20 px-4 py-3 text-sm"
                  >
                    <div className="font-semibold">
                      {build.idea || build.build_id}{' '}
                      <span className="text-[var(--color-cyan)]">grade {build.grade}</span>
                    </div>
                    <div className="text-xs text-[var(--color-mist)]">
                      {build.build_id} · success {build.success_pct}% · mvp{' '}
                      {build.mvp_ready ? 'yes' : 'no'}
                    </div>
                  </div>
                ))}
                {(data.analytics.builds || []).length === 0 && (
                  <p className="text-sm text-[var(--color-mist)]">
                    Launch a discovery build to score the first app.
                  </p>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm text-[var(--color-mist)]">No analytics yet.</p>
          )}
        </section>
      </main>
    </AppChrome>
  )
}
